import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DESIRED_COLOR_OPTIONS,
  DESIRED_STYLE_OPTIONS,
  MATERIAL_OPTIONS,
  ROOM_MODE_OPTIONS,
  ROOM_TYPE_OPTIONS,
} from '../../constants/style-settings';
import { CollapsableCard } from '../collapsable-card';
import { DesiredColorSettings } from './desired-color-settings';
import { DesiredStyleSettings } from './desired-style-settings';
import { MaterialSettings } from './materials-settings';
import { RoomModeSettings } from './room-mode-settings';
import { RoomTypeSettings } from './room-type-settings';

export type StyleSettings = {
  roomMode?: ROOM_MODE_OPTIONS;
  roomType?: ROOM_TYPE_OPTIONS;
  desiredStyle?: DESIRED_STYLE_OPTIONS;
  desiredColor?: DESIRED_COLOR_OPTIONS[];
  materials?: MATERIAL_OPTIONS[];
};

interface StyleSettingsProps {
  settings: StyleSettings;
  onChangeSettings: Dispatch<SetStateAction<StyleSettings>>;
}

export const StyleSettings = ({ settings, onChangeSettings }: StyleSettingsProps) => {
  const { t } = useTranslation();

  const handleUpdateSettings = (
    key: keyof StyleSettings,
    val:
      | ROOM_MODE_OPTIONS
      | ROOM_TYPE_OPTIONS
      | DESIRED_COLOR_OPTIONS
      | DESIRED_STYLE_OPTIONS
      | MATERIAL_OPTIONS
  ) => {
    if (key === 'desiredColor' || key === 'materials') {
      const exist =
        (settings[key] as (MATERIAL_OPTIONS | DESIRED_COLOR_OPTIONS)[])?.indexOf(
          val as MATERIAL_OPTIONS | DESIRED_COLOR_OPTIONS
        ) > -1;

      if (exist) {
        onChangeSettings((prev) => ({
          ...prev,
          [key]: (prev[key] as (MATERIAL_OPTIONS | DESIRED_COLOR_OPTIONS)[])?.filter(
            (item) => item !== val
          ),
        }));
      } else {
        onChangeSettings((prev) => ({
          ...prev,
          [key]: [...(prev?.[key] ?? []), val],
        }));
      }
    } else {
      onChangeSettings((prev) => ({
        ...prev,
        [key]: val,
      }));
    }
  };

  return (
    <CollapsableCard title={t('styleSettings')}>
      <div className="flex flex-col gap-2">
        <RoomModeSettings roomMode={settings.roomMode} onUpdate={handleUpdateSettings} />
        <RoomTypeSettings roomType={settings.roomType} onUpdate={handleUpdateSettings} />
        <DesiredStyleSettings
          desiredStyle={settings.desiredStyle}
          onUpdate={handleUpdateSettings}
        />
        <DesiredColorSettings colors={settings.desiredColor} onUpdate={handleUpdateSettings} />
        <MaterialSettings materials={settings.materials} onUpdate={handleUpdateSettings} />
      </div>
    </CollapsableCard>
  );
};
